import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, Loader2, Save, Timer, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin bulk-timing picker — select cues + apply a `cue_seconds` value in one shot.
 *
 * Workflow:
 *   1. Filter by category + search to scope.
 *   2. Tick the cues you want to set to the same time.
 *   3. Hit a preset (1.5/2.0/2.5/.../5.0) OR type a custom number → click "Apply to N selected".
 *   4. Or click "Clear override on N selected" to revert those cues back to the auto-computed time.
 *
 * The auto-computed default (from punches/movement_count) is shown next to each cue
 * so the coach can see what they'd be overriding before they do it.
 */

const PRESET_SECONDS = [1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0];
const CAT_COLOR = {
  punch: "#FF331F", defense: "#3B82F6", footwork: "#A855F7",
  feint: "#F59E0B", atmosphere: "#10B981", range: "#EC4899",
};

// Mirror of the frontend `cueBaseSeconds()` so we can show the auto-computed default
// without a roundtrip. Kept in sync manually with `/app/frontend/src/lib/roninBox.js`.
const PUNCH_BASE = [0, 1.7, 2.4, 3.0, 3.6, 4.0, 4.4, 4.7, 5.0];
const MOVE_BASE = [0, 1.6, 2.3, 2.9, 3.4, 3.8];
function autoSecondsFor(cue) {
  const punches = Math.max(0, Math.floor(Number(cue?.punches) || 0));
  if (punches > 0) return PUNCH_BASE[Math.min(punches, PUNCH_BASE.length - 1)];
  const moves = Math.max(0, Math.floor(Number(cue?.movement_count) || 0));
  if (moves > 0) return MOVE_BASE[Math.min(moves, MOVE_BASE.length - 1)];
  return 2.0;
}

export default function RoninTimingPicker() {
  const [cues, setCues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [filterCat, setFilterCat] = useState("all");
  const [search, setSearch] = useState("");
  const [customSeconds, setCustomSeconds] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [cuesRes, catsRes] = await Promise.all([
        api.get("/admin/ronin-box/cues"),
        api.get("/admin/ronin-box/categories"),
      ]);
      setCues(cuesRes.data || []);
      setCategories(catsRes.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(load, 0);
    return () => clearTimeout(t);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cues.filter((c) => {
      if (filterCat !== "all" && c.category !== filterCat) return false;
      if (q && !c.text.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [cues, filterCat, search]);

  const toggle = (cueId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cueId)) next.delete(cueId); else next.add(cueId);
      return next;
    });
  };

  const selectAllFiltered = () => setSelected(new Set(filtered.map((c) => c.id)));
  const clearSelection = () => setSelected(new Set());

  const applySeconds = async (value) => {
    if (selected.size === 0) {
      toast.error("Select at least one cue first.");
      return;
    }
    const updates = [...selected].map((id) => ({ id, cue_seconds: value }));
    setSaving(true);
    try {
      const { data } = await api.post("/admin/ronin-box/cues/batch-seconds", { updates });
      if (value === null) {
        toast.success(`Cleared override on ${data.updated} cue${data.updated === 1 ? "" : "s"} (${data.skipped} already auto)`);
      } else {
        toast.success(`Set ${data.updated} cue${data.updated === 1 ? "" : "s"} to ${value}s (${data.skipped} unchanged)`);
      }
      clearSelection();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const applyCustom = () => {
    const v = parseFloat(customSeconds);
    if (!Number.isFinite(v) || v < 0.5 || v > 15) {
      toast.error("Enter a value between 0.5 and 15 seconds.");
      return;
    }
    applySeconds(v);
  };

  // Build per-row data for the table (memoized to keep render quick)
  const rows = useMemo(() => {
    return filtered.map((c) => {
      const auto = autoSecondsFor(c);
      const override = (c.cue_seconds != null && c.cue_seconds > 0) ? c.cue_seconds : null;
      const effective = override ?? auto;
      return { cue: c, auto, override, effective };
    });
  }, [filtered]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="border-b border-[#27272A] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/admin/ronin-box" className="text-zinc-400 hover:text-white transition-colors" data-testid="back-to-ronin-admin">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Ronin Box</p>
            <h1 className="font-display text-2xl uppercase tracking-tight">Bulk timing</h1>
          </div>
        </div>
        <div className="text-[11px] text-zinc-500">
          {cues.length} cues · <span className="text-[#FF331F] font-bold">{selected.size}</span> selected
        </div>
      </div>

      <div className="p-6 max-w-6xl mx-auto">
        <p className="text-sm text-zinc-400 mb-5 max-w-2xl">
          Pick the cues you want to give a custom time-to-complete, then click a preset (or enter a custom value) to apply it to all of them at once. <span className="text-zinc-500">Auto</span> column shows what each cue would get without an override (from punches/movement). <span className="text-zinc-300 font-bold">Override</span> wins if set.
        </p>

        {/* Bulk action bar — sticky so it stays in view while scrolling */}
        <div className="sticky top-0 z-10 bg-[#0A0A0A] border border-[#27272A] rounded-md p-4 mb-5">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Apply to {selected.size} selected</p>
          <div className="flex flex-wrap items-center gap-2">
            {PRESET_SECONDS.map((s) => (
              <Button
                key={s}
                onClick={() => applySeconds(s)}
                disabled={saving || selected.size === 0}
                className="h-9 px-3 bg-[#FF331F] hover:bg-[#FF331F]/85 disabled:bg-[#0F0F0F] disabled:text-zinc-600 text-white font-bold uppercase tracking-widest text-xs rounded-sm"
                data-testid={`timing-preset-${s}`}
              >
                {s}s
              </Button>
            ))}
            <div className="inline-flex items-center gap-2 border border-[#27272A] bg-[#050505] rounded-sm pl-2 pr-1 h-9">
              <Input
                type="number"
                step="0.1"
                min="0.5"
                max="15"
                value={customSeconds}
                onChange={(e) => setCustomSeconds(e.target.value)}
                placeholder="Custom"
                className="w-20 bg-transparent border-0 text-white rounded-sm h-7 text-sm focus-visible:ring-0"
                data-testid="timing-custom-input"
              />
              <Button
                onClick={applyCustom}
                disabled={saving || selected.size === 0 || !customSeconds}
                className="h-7 px-2 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-black font-bold uppercase tracking-widest text-[10px] rounded-sm"
                data-testid="timing-apply-custom"
              >
                Apply
              </Button>
            </div>
            <span className="text-zinc-700">·</span>
            <Button
              onClick={() => applySeconds(null)}
              disabled={saving || selected.size === 0}
              variant="outline"
              className="h-9 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 uppercase tracking-widest text-xs font-bold disabled:opacity-40"
              data-testid="timing-clear-override"
            >
              Clear override (use auto)
            </Button>
            {saving && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search cue text…"
              className="bg-[#0A0A0A] border-[#27272A] text-white rounded-sm pl-9"
              data-testid="timing-search"
            />
          </div>
          <button
            onClick={() => setFilterCat("all")}
            className={`px-3 py-2 text-[10px] uppercase tracking-widest font-bold border rounded-sm transition-colors ${filterCat === "all" ? "border-[#FF331F] bg-[#FF331F]/15 text-[#FF331F]" : "border-[#27272A] bg-[#050505] text-zinc-500 hover:text-white"}`}
            data-testid="timing-filter-all"
          >
            All {cues.length}
          </button>
          {categories.map((cat) => {
            const count = cues.filter((c) => c.category === cat.id).length;
            if (count === 0) return null;
            return (
              <button
                key={cat.id}
                onClick={() => setFilterCat(cat.id)}
                className={`px-3 py-2 text-[10px] uppercase tracking-widest font-bold border rounded-sm transition-colors flex items-center gap-1.5 ${filterCat === cat.id ? "border-[#FF331F] bg-[#FF331F]/15 text-[#FF331F]" : "border-[#27272A] bg-[#050505] text-zinc-500 hover:text-white"}`}
                data-testid={`timing-filter-${cat.id}`}
              >
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: CAT_COLOR[cat.id] || "#888" }} />
                {cat.label} {count}
              </button>
            );
          })}
        </div>

        {/* Select-all controls for the current filter */}
        <div className="flex items-center gap-3 mb-3 text-[11px] text-zinc-500">
          <button onClick={selectAllFiltered} className="text-cyan-400 hover:text-cyan-300 uppercase tracking-widest font-bold" data-testid="timing-select-all-filtered">
            Select all {filtered.length} visible
          </button>
          <span className="text-zinc-700">·</span>
          <button onClick={clearSelection} className="text-zinc-400 hover:text-white uppercase tracking-widest font-bold" data-testid="timing-clear-selection">
            Clear selection
          </button>
        </div>

        {/* Cue list */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-500">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading cues…
          </div>
        ) : (
          <div className="border border-[#27272A] rounded-md overflow-hidden bg-[#0A0A0A]" data-testid="timing-cue-list">
            <table className="w-full">
              <thead className="bg-[#050505] border-b border-[#27272A]">
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="p-3 w-10"></th>
                  <th className="p-3 text-left">Text</th>
                  <th className="p-3 text-left">Category</th>
                  <th className="p-3 text-right">Punches</th>
                  <th className="p-3 text-right">Movements</th>
                  <th className="p-3 text-right">Auto</th>
                  <th className="p-3 text-right">Override</th>
                  <th className="p-3 text-right">Effective</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-zinc-600 py-10">No cues match the current filter.</td></tr>
                )}
                {rows.map(({ cue: c, auto, override, effective }) => {
                  const isSelected = selected.has(c.id);
                  return (
                    <tr
                      key={c.id}
                      onClick={() => toggle(c.id)}
                      className={`border-b border-[#27272A] cursor-pointer transition-colors ${isSelected ? "bg-[#FF331F]/10 hover:bg-[#FF331F]/15" : "hover:bg-[#0F0F0F]"}`}
                      data-testid={`timing-row-${c.id}`}
                    >
                      <td className="p-3">
                        {isSelected
                          ? <CheckSquare className="h-4 w-4 text-[#FF331F]" />
                          : <Square className="h-4 w-4 text-zinc-700" />}
                      </td>
                      <td className="p-3 text-white font-bold">{c.text}</td>
                      <td className="p-3">
                        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-zinc-400">
                          <span className="inline-block w-2 h-2 rounded-full" style={{ background: CAT_COLOR[c.category] || "#888" }} />
                          {c.category}
                        </span>
                      </td>
                      <td className="p-3 text-right text-zinc-400">{c.punches > 0 ? c.punches : "—"}</td>
                      <td className="p-3 text-right text-zinc-400">{c.movement_count > 0 ? c.movement_count : "—"}</td>
                      <td className="p-3 text-right text-zinc-500 text-xs">{auto.toFixed(1)}s</td>
                      <td className="p-3 text-right">
                        {override != null
                          ? <span className="text-[#FF331F] font-bold text-xs">{override.toFixed(1)}s</span>
                          : <span className="text-zinc-700 text-xs">—</span>}
                      </td>
                      <td className="p-3 text-right">
                        <span className={`inline-flex items-center gap-1 text-xs font-bold ${override != null ? "text-[#FF331F]" : "text-white"}`}>
                          <Timer className="h-3 w-3" /> {effective.toFixed(1)}s
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
